// @flow

import React, { Component } from 'react';
import _ from 'lodash';

import './style.scss';

import I18n from 'PMT/i18n/I18n';

// -- constants
import Events from 'constants/Events';
import RoutingEnum from 'constants/RoutingEnum';

// -- utils
import { ViewUtils } from 'PMT/utils';
import Listener from 'PMT/flux/Listener';

import SecurityContext from 'utils/permissions/SecurityContext';

// -- actions
import { RestaurantMenuAction, RouteAction } from 'actions';

// -- stores
import SessionStore from 'stores/SessionStore';
import { RestaurantMenuStore, OrderSettingsStore } from 'stores';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import { Menu, RestaurantMenu, MenuPart } from 'PMT/entities/menu';

import { OrderSettings } from 'PMT/entities/order';

// -- components
import RestaurantMenuPage from '../RestaurantMenuPage';
import AbstractComponent from 'components/AbstractComponent';
import Ariane from 'PMT/components/Ariane';
import MenuPartEditView from './MenuPartEditView';

const Errors = {
  MENU_NOT_FOUND: 'menu_not_found',
};

export default class MenuPartEditPage extends AbstractComponent {
  state = {
    restaurantMenu: RestaurantMenuStore.getRestaurantMenu(),
    menu: null,
    menuPart: this.isEditMode() ? null : new MenuPart(),
    apiError: null,
    // A view error, will contain an `Errors`
    error: null,
  };

  componentDidMount() {
    RestaurantMenuStore.addListeners(this.restaurantMenuStoreListeners);

    if (!SecurityContext.restaurantMenu.canEdit) {
      // TODO: redirect
    }

    if (_.isNull(this.state.restaurantMenu)) {
      RestaurantMenuAction.loadRestaurantMenu();
    } else if (this.isEditMode()) {
      this.loadMenu();
    }
  }

  componentWillUnmount() {
    RestaurantMenuStore.removeListeners(this.restaurantMenuStoreListeners);
  }

  get restaurantMenuStoreListeners(): Array<Listener> {
    return [
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_SUCCESS, this.onChange),
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_ERROR, this.onError),

      new Listener(
        Events.ON_UPDATE_RESTAURANT_MENU_SUCCESS,
        this.onMenuUpdated
      ),
      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_ERROR, this.onError),
      new Listener(
        Events.ON_UPDATE_RESTAURANT_MENU_MENU_PART_ERROR,
        this.onError
      ),

      new Listener(
        Events.ON_UPDATE_RESTAURANT_MENU_MENU_PART_SUCCESS,
        this.onChange
      ),

      new Listener(
        Events.ON_UPDATE_RESTAURANT_MENU_MENU_PART_CATEGORY_SUCCESS,
        this.onMenuUpdated
      ),
      new Listener(
        Events.ON_UPDATE_RESTAURANT_MENU_MENU_PART_CATEGORY_ERROR,
        this.onError
      ),
    ];
  }

  get partId() {}

  loadMenu() {
    const menu: Menu = this.getMenuOnRestaurantMenu(this.state.restaurantMenu);

    if (_.isNull(menu)) {
      // TODO: redirect to 404.
      this.setState({
        error: Errors.MENU_NOT_FOUND,
        loading: false,
      });
    } else {
      const menuPart: MenuPart = menu.getPartWithId(this.partId);

      this.setState({
        menu,
        menuPart,
        loading: false,
        error: null,
      });
    }
  }

  // -- Event functions

  onChange = () => {
    const restaurantMenu: RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();

    if (this.isEditMode()) {
      const menu: Menu = this.getMenuOnRestaurantMenu(restaurantMenu);
      if (_.isNull(menu)) {
        // TODO: redirect to 404.
        this.setState({
          error: Errors.MENU_NOT_FOUND,
          loading: false,
        });
      } else {
        const menuPart: MenuPart = menu.getPartWithId(this.partId);

        this.setState({
          restaurantMenu,
          menu,
          menuPart,
          loading: false,
          error: null,
        });
      }
    }
  };

  handleRestaurantMenuChange = restaurantMenu => {
    this.setState({
      restaurantMenu,
    });
  };

  onMenuUpdated = () => {
    if (this.isEditMode()) {
      const restaurantMenu: RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();
      const menu: Menu = this.getMenuOnRestaurantMenu(restaurantMenu);
      const menuPart: MenuPart = menu.getPartWithId(this.partId);

      this.setState({
        restaurantMenu,
        menu,
        menuPart,
        loading: false,
      });
    }
  };

  onError = () => {
    this.hideLoading();
    this.setState({
      apiError: RestaurantMenuStore.getApiError(),
    });
  };

  get menu(): Menu {
    return this.state.menu;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.state.restaurantMenu;
  }

  get menuPart(): MenuPart {
    return this.state.menuPart;
  }

  get partId() {
    return this.props.params.menuPartId;
  }

  isEditMode() {
    return !_.isUndefined(this.props.params.menuPartId);
  }

  getMenuOnRestaurantMenu(restaurantMenu) {
    const menuId = this.props.params.menuId;
    const menu: Menu = restaurantMenu.getMenuWithId(menuId);

    return menu;
  }

  // we update the whole menuPart
  handleAddCategory = (menuPart, category) => {
    const menu = this.state.menu;
    this.showLoading();
    RestaurantMenuAction.updateMenuPart(menu, menuPart);
  };

  handleDeleteCategory = (menuPart, category) => {
    const menu = this.state.menu;
    this.showLoading();
    RestaurantMenuAction.deleteMenuPartCategory(menu, menuPart, category);
  };

  handleUploadPartCategoryImage = (category, image) => {
    this.showLoading();
    RestaurantMenuAction.uploadMenuPartCategoryImage(
      this.menu,
      this.menuPart,
      category,
      image
    );
  };

  handleRemovePartCategoryImage = category => {
    this.showLoading();
    RestaurantMenuAction.removeMenuPartCategoryImage(
      this.menu,
      this.menuPart,
      category
    );
  };

  // -- renderers

  renderBody() {
    if (!_.isNull(this.apiError)) {
      return this.renderApiErrorBlock();
    } else if (_.isNull(this.menu) || _.isNull(this.restaurantMenu)) {
      return this.renderLoading(true);
    } else if (!_.isNull(this.menu) && _.isNull(this.menuPart)) {
      return <h1>Not found</h1>;
    }

    if (!_.isNull(this.state.error)) {
      return (
        <div>
          {this.state.error}
        </div>
      );
    }

    const arianeIcon = 'fa-shopping-cart';

    const arianeDetails = [
      {
        name: I18n.tr('menu.order'),
        link: null,
      },
      {
        name: I18n.tr('menu.order.restaurant_menu'),
        link: RoutingEnum.RESTAURANT_MENU,
        query: {
          tab: RestaurantMenuPage.Tabs.CATEGORIES,
        },
      },
      {
        name: I18n.tr('menu.order.restaurant_menu.menus'),
        link: RoutingEnum.RESTAURANT_MENU,
        query: {
          tab: RestaurantMenuPage.Tabs.MENUS,
        },
      },
    ];

    if (this.isEditMode()) {
      arianeDetails.push({
        name: this.menu.name,
        link: RoutingEnum.RESTAURANT_MENU_MENU_DETAIL,
        params: {
          menuId: this.menu.id,
        },
      });

      arianeDetails.push({
        name: this.menuPart.name,
        link: null,
      });
      arianeDetails.push({
        name: I18n.tr('menu.order.restaurant_menu.edition'),
        link: null,
      });
    }

    return (
      <div>
        <Ariane icon={arianeIcon} details={arianeDetails} />

        <MenuPartEditView
          menuPart={this.menuPart}
          menu={this.menu}
          restaurantMenu={this.restaurantMenu}
          onUpdate={this.onUpdateMenu}
          isEditMode={this.isEditMode()}
          orderSettings={OrderSettingsStore.getOrderSettings()}
          onRestaurantMenuChange={this.handleRestaurantMenuChange}
          onAddCategory={this.handleAddCategory}
          onDeleteCategory={this.handleDeleteCategory}
          onRemovePartCategoryImage={this.handleRemovePartCategoryImage}
          onUploadPartCategoryImage={this.handleUploadPartCategoryImage}
        />
      </div>
    );
  }

  render() {
    try {
      return (
        <div className="menu_part_detail_page">
          {this.renderBody()}
          {this.renderLoading()}
        </div>
      );
    } catch (e) {
      console.error(e);
    }
  }
}



// WEBPACK FOOTER //
// ./scripts/pages/menu/menuPartEdit/MenuPartEditPage.js